import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/* La mesure des visites du site (école, 23.10.2026 : « combien de gens viennent, combien
   de temps ils restent »). Les chiffres se lisent dans la console, Tableau de bord → Le site.

   Sans cookie et sans service extérieur : une ligne par page vue vers notre serveur
   (POST /api/mesure), puis le temps passé sur la page, onglet visible, quand on la quitte.
   Une visite = cet onglet, jusqu'à 30 minutes sans rien (identifiant tiré au hasard dans
   sessionStorage). Le serveur ne garde jamais l'adresse IP (voir backend/console/mesure_site.py).

   Ne mesure pas : les applications (console, espace famille, prof, mode école), les
   navigateurs de l'école (connectés à la console) et les robots. */

const SERVEUR = process.env.REACT_APP_BACKEND_URL || "";
const APPLIS = ["/console", "/admin", "/espace", "/prof", "/direction"];
const VISITE = "mesure_visite";
const DEJA_VENU = "mesure_deja_venu";
const PAUSE_MAX = 30 * 60 * 1000;
const ROBOT = /bot|crawl|spider|headless|lighthouse|prerender/i;

const lireSession = (cle) => {
  try { return JSON.parse(sessionStorage.getItem(cle)); } catch { return null; }
};
const ecrireSession = (cle, valeur) => {
  try { sessionStorage.setItem(cle, JSON.stringify(valeur)); } catch { /* navigation privée */ }
};

function exclu(chemin) {
  if (!SERVEUR || typeof navigator === "undefined") return true;
  if (navigator.webdriver || ROBOT.test(navigator.userAgent || "")) return true;
  if (APPLIS.some((a) => chemin === a || chemin.startsWith(`${a}/`))) return true;
  try {
    if (localStorage.getItem("admin_token")) return true;   // l'école elle-même
  } catch { /* stockage bloqué : on mesure */ }
  return false;
}

function envoyer(donnees) {
  const corps = JSON.stringify(donnees);
  const url = `${SERVEUR}/api/mesure`;
  try {
    // text/plain : pas de demande préalable (CORS) ; part même si l'onglet se ferme.
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([corps], { type: "text/plain" }))) return;
  } catch { /* on essaie autrement */ }
  try {
    fetch(url, { method: "POST", body: corps, keepalive: true, mode: "no-cors",
      headers: { "Content-Type": "text/plain" } }).catch(() => {});
  } catch { /* tant pis : une page de moins dans les chiffres */ }
}

function nouvelIdentifiant() {
  const octets = new Uint8Array(12);
  try { crypto.getRandomValues(octets); } catch { return Math.random().toString(36).slice(2, 18).padEnd(16, "0"); }
  return Array.from(octets, (o) => o.toString(36).padStart(2, "0")).join("").slice(0, 20);
}

/* La visite en cours dans cet onglet, ou une nouvelle après 30 minutes sans rien. */
function visite() {
  const maintenant = Date.now();
  let v = lireSession(VISITE);
  if (!v || !v.id || maintenant - (v.dernier || 0) > PAUSE_MAX) v = { id: nouvelIdentifiant(), n: -1 };
  v.dernier = maintenant;
  return v;
}

function lieuConnu() {
  // Le lieu donné par Netlify, déjà demandé par l'assistant du site dans cet onglet.
  const geo = lireSession("assistant_lieu");
  return geo && geo.pays ? geo : null;
}

async function lieuNetlify() {
  if (process.env.REACT_APP_GEO_NETLIFY === "false") return null;
  const connu = lieuConnu();
  if (connu) return connu;
  try {
    const arret = new AbortController();
    const minuterie = setTimeout(() => arret.abort(), 1200);
    const r = await fetch("/lieu-visiteur", { signal: arret.signal, cache: "no-store" });
    clearTimeout(minuterie);
    const d = r.ok && (r.headers.get("content-type") || "").includes("json") ? await r.json() : null;
    const geo = d && d.pays ? { ville: d.ville || "", region: d.region || "", pays: d.pays } : {};
    ecrireSession("assistant_lieu", geo);
    return geo.pays ? geo : null;
  } catch { return null; }
}

export default function MesureVisites() {
  const { pathname } = useLocation();

  useEffect(() => {
    const chemin = pathname || "/";
    if (exclu(chemin)) return undefined;
    const v = visite();
    v.n += 1;
    ecrireSession(VISITE, v);
    const n = v.n;
    const premiere = n === 0;

    let deja = false;
    try { deja = localStorage.getItem(DEJA_VENU) === "1"; } catch { /* rien */ }
    const ligne = { t: "vue", v: v.id, n, p: chemin, w: window.innerWidth || 0 };
    const partir = (lieu) => envoyer(premiere ? {
      ...ligne,
      r: document.referrer || "",
      u: (() => {
        const q = new URLSearchParams(window.location.search);
        return { source: q.get("utm_source") || "", medium: q.get("utm_medium") || "", campaign: q.get("utm_campaign") || "" };
      })(),
      l: navigator.language || "",
      f: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; } })(),
      rv: deja,
      ...(lieu ? { lieu } : {}),
    } : ligne);
    if (premiere) lieuNetlify().then(partir); else partir(null);
    try { localStorage.setItem(DEJA_VENU, "1"); } catch { /* rien */ }

    // Le temps passé sur cette page, onglet visible seulement.
    let visibleDepuis = document.visibilityState === "visible" ? Date.now() : null;
    let cumul = 0;
    const arreter = () => {
      if (visibleDepuis) { cumul += Date.now() - visibleDepuis; visibleDepuis = null; }
    };
    const solder = () => {
      arreter();
      const s = Math.round(cumul / 1000);
      cumul = 0;
      if (s > 0) envoyer({ t: "fin", v: v.id, n, p: chemin, s });
    };
    const surVisibilite = () => {
      if (document.visibilityState === "hidden") solder();
      else {
        visibleDepuis = Date.now();
        const encore = lireSession(VISITE);
        if (encore && encore.id === v.id) ecrireSession(VISITE, { ...encore, dernier: Date.now() });
      }
    };
    document.addEventListener("visibilitychange", surVisibilite);
    window.addEventListener("pagehide", solder);
    return () => {
      document.removeEventListener("visibilitychange", surVisibilite);
      window.removeEventListener("pagehide", solder);
      solder();
    };
  }, [pathname]);

  return null;
}
